"use client";

import { CircleDashed, CirclePlus } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import Image from "next/image";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { cn } from "../../lib";
import {
	type EntityItemHighlight,
	entityHighlightBorderBackground,
} from "../../lib/entity-item-highlight";
import {
	ENTITY_TILE_IMAGE_IN_DELAY_MS,
	ENTITY_TILE_IMAGE_IN_MS,
	ENTITY_TILE_IMAGE_IN_OVERSCALE,
	ENTITY_TILE_IMAGE_PLACEHOLDER_OUT_MS,
} from "../../lib/entity-tile-image-motion";
import { OctagonBadge } from "../octagon-badge";
import {
	CONTENT_TOOLTIP_ABILITY_DARK,
	ContentTooltip,
} from "../design-system/tooltip";

/** Flat-top octagon: rotate pointy-top chrome by 22.5°. */
export const EQUIP_SLOT_FLAT_TOP_ROTATE_DEG = 22.5;

const IMAGE_MOTION_EASE: [number, number, number, number] = [0.4, 0, 0.2, 1];

export interface EquipSlotBadgeProps {
	size?: number;
	/** Accessible name for empty or filled slot. */
	ariaLabel: string;
	/** Equipped item art (optional). */
	itemImageSrc?: string | null;
	/**
	 * Stable identity for enter/exit motion (item instance id). Prefer this over
	 * `itemImageSrc` — signed icon URLs rotate on every inventory refetch.
	 */
	itemInstanceId?: string | null;
	/**
	 * Rarity / potential highlight — same gradients as inventory tile frames.
	 * Applied on the native stroke ring (thin, concentric with glass + face).
	 */
	highlight?: EntityItemHighlight | null;
	tooltip?: ReactNode;
	className?: string;
	/** Double-click handler (e.g. unequip from paper doll). */
	onDoubleClick?: () => void;
	/**
	 * Empty-slot glyph: `dashed` (default) or `plus` when backpack has
	 * eligible gear for this slot.
	 */
	emptyVariant?: "dashed" | "plus";
	/** Single-click when empty + `plus` (open equip picker). */
	onEmptyClick?: () => void;
}

/**
 * Paper-doll equipment slot — flat-top octagon chrome with optional item art + tooltip.
 * Item art uses the same scale/fade in·out timing as {@link EntityTileImage} load reveal.
 */
export function EquipSlotBadge({
	size = 52,
	ariaLabel,
	itemImageSrc,
	itemInstanceId = null,
	highlight = null,
	tooltip,
	className,
	onDoubleClick,
	emptyVariant = "dashed",
	onEmptyClick,
}: EquipSlotBadgeProps) {
	const iconSize = Math.round(size * 0.55);
	/** Smaller than item art so empty glyphs sit clearly inside the face. */
	const emptyIconSize = Math.round(size * 0.36);
	const resolvedImageSrc =
		typeof itemImageSrc === "string" && itemImageSrc.length > 0
			? itemImageSrc
			: null;
	const motionKey =
		(typeof itemInstanceId === "string" && itemInstanceId.length > 0
			? itemInstanceId
			: null) ??
		(resolvedImageSrc
			? resolvedImageSrc.split("?")[0] ?? resolvedImageSrc
			: null);
	const isFilled = Boolean(resolvedImageSrc && motionKey);
	const showPlus = !isFilled && emptyVariant === "plus";
	const canEmptyClick = showPlus && typeof onEmptyClick === "function";
	const layerOverrides = useMemo(() => {
		if (!highlight) {
			return undefined;
		}
		return {
			stroke: {
				backgroundColor: "transparent",
				backgroundImage: entityHighlightBorderBackground(highlight),
			},
		};
	}, [highlight]);

	const EmptyIcon = showPlus ? CirclePlus : CircleDashed;

	const slot = (
		<div
			className={cn(
				"relative flex shrink-0 items-center justify-center",
				className,
			)}
			style={{ width: size, height: size }}
			data-equip-slot-badge=""
			data-equip-slot-highlight={highlight ?? undefined}
			data-equip-slot-empty={
				isFilled ? undefined : showPlus ? "plus" : "dashed"
			}
		>
			<div
				className="absolute inset-0 flex items-center justify-center"
				style={{
					transform: `rotate(${EQUIP_SLOT_FLAT_TOP_ROTATE_DEG}deg)`,
				}}
			>
				<OctagonBadge
					size={size}
					surface="button"
					layoutFootprint="visual"
					dropShadow={false}
					interactive
					ariaHidden={false}
					ariaLabel={ariaLabel}
					layerOverrides={layerOverrides}
				/>
			</div>
			{isFilled ? null : (
				<div
					className="pointer-events-none absolute inset-0 z-[5] grid place-items-center"
					aria-hidden
					data-equip-slot-empty-icon=""
				>
					<EmptyIcon
						className={cn(
							"-translate-y-px shrink-0",
							showPlus ? "opacity-100" : "opacity-30",
						)}
						style={{ width: emptyIconSize, height: emptyIconSize }}
						strokeWidth={2}
					/>
				</div>
			)}
			<AnimatePresence mode="popLayout">
				{isFilled && resolvedImageSrc && motionKey ? (
					<motion.div
						key={motionKey}
						className="pointer-events-none absolute inset-0 z-[6] flex items-center justify-center"
						aria-hidden
						initial={{ scale: 0, opacity: 0 }}
						animate={{
							scale: ENTITY_TILE_IMAGE_IN_OVERSCALE,
							opacity: 1,
							transition: {
								duration: ENTITY_TILE_IMAGE_IN_MS / 1000,
								delay: ENTITY_TILE_IMAGE_IN_DELAY_MS / 1000,
								ease: IMAGE_MOTION_EASE,
							},
						}}
						exit={{
							scale: 0,
							opacity: 0,
							transition: {
								duration:
									ENTITY_TILE_IMAGE_PLACEHOLDER_OUT_MS / 1000,
								ease: IMAGE_MOTION_EASE,
							},
						}}
					>
						<div
							className="relative"
							style={{ width: iconSize, height: iconSize }}
						>
							{/*
							  Plain Image: load-skeleton is skipped so parent motion
							  owns the scale/fade (same curve as EntityTileImage reveal).
							*/}
							<Image
								src={resolvedImageSrc}
								alt=""
								fill
								className="object-contain [-webkit-user-drag:none] select-none"
								sizes={`${iconSize}px`}
								unoptimized
								draggable={false}
							/>
						</div>
					</motion.div>
				) : null}
			</AnimatePresence>
		</div>
	);

	const interactive = Boolean(onDoubleClick || canEmptyClick);
	if (tooltip == null) {
		if (!interactive) {
			return slot;
		}
		return (
			<button
				type="button"
				className="relative inline-flex shrink-0 cursor-pointer border-0 bg-transparent p-0"
				aria-label={ariaLabel}
				onClick={canEmptyClick ? onEmptyClick : undefined}
				onDoubleClick={onDoubleClick}
			>
				{slot}
			</button>
		);
	}

	return (
		<ContentTooltip
			{...CONTENT_TOOLTIP_ABILITY_DARK}
			side="top"
			align="center"
			content={tooltip}
		>
			<button
				type="button"
				className={cn(
					"relative inline-flex shrink-0 border-0 bg-transparent p-0",
					interactive ? "cursor-pointer" : "cursor-default",
				)}
				aria-label={ariaLabel}
				onClick={canEmptyClick ? onEmptyClick : undefined}
				onDoubleClick={onDoubleClick}
			>
				{slot}
			</button>
		</ContentTooltip>
	);
}
